import React, { ComponentProps } from 'react';
import { PersonInformation } from '../../../../components/molecules/PersonInformation';
import { PersonalData } from '../../../../_lib/components/PersonalData/PersonalData';
import { ContentParagraph } from '../../../../_lib/components/ContentParagraph';
import { CollapsibleBlock, CollapsibleBlockModifier } from '../../../../components/molecules/CollapsibleBlock';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';
import { PersonInformationProps } from '../../../../components/molecules/PersonInformation/PersonInformation';
import { asFetchableView } from '../../../../_lib/ViewFetcher';
import { PageTreeWithFilters } from '../PageTreeWithFilters';

export type ExecutiveOrganizationChart = {
  gvpInfo: {
    pageTitle: string;
    description: string;
    contactPersons: Omit<PersonInformationProps & { id: string }, 'modifier'>[];
    remainingPersons: Omit<PersonalData & { id: string }, 'modifier'>[];
  };
  pageTreeWithFilters?: PageTreeWithFilters;
};

export function ExecutiveOrganizationChart({ gvpInfo, pageTreeWithFilters }: ExecutiveOrganizationChart) {
  const { t } = useSonTranslation('StructuralPageToPage');
  const id = useHtmlValidId();
  const { pageTitle, description, contactPersons, remainingPersons } = gvpInfo;

  const childPages = pageTreeWithFilters?.connectedPages?.at(0)?.childPages;

  return (
    <div className="structural-page-to-page-tree__executive-organization-chart">
      <div className="structural-page-to-page-tree__executive-organization-chart-container">
        <div className="structural-page-to-page-tree__header">
          {pageTitle ? <h3 className="structural-page-to-page-tree__headline font-bold-16-24">{pageTitle}</h3> : null}
          {description ? <ContentParagraph>{description}</ContentParagraph> : null}
        </div>
        {contactPersons && contactPersons.length > 0 ? (
          <ul className="structural-page-to-page-tree__executive-organization-chart-grid">
            {contactPersons.map(contactPerson => (
              <li key={contactPerson.id}>
                <PersonInformation {...contactPerson} />
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      {remainingPersons && remainingPersons.length > 0 ? (
        <CollapsibleBlock
          hierarchy={3}
          title={t('allPersons')}
          closed={false}
          id={id}
          modifier={[CollapsibleBlockModifier.LARGE_HEADLINE, CollapsibleBlockModifier.LIGHT]}
        >
          <div className="structural-page-to-page-tree__executive-organization-chart-container">
            <ul className="structural-page-to-page-tree__executive-organization-chart-grid">
              {remainingPersons.map(remainingPerson => (
                <li key={remainingPerson.id}>
                  <PersonalData {...remainingPerson} />
                </li>
              ))}
            </ul>
          </div>
        </CollapsibleBlock>
      ) : null}
      {/* We don't want any output here (even no "there is no data"-warning), unless there is any data. */}
      {childPages && childPages.length > 0 ? (
        <PageTreeWithFilters {...pageTreeWithFilters} eocCounterModifier={true} />
      ) : null}
    </div>
  );
}

export const ExecutiveOrganizationChartView = asFetchableView(ExecutiveOrganizationChart);
export type ExecutiveOrganizationChartView = ComponentProps<typeof ExecutiveOrganizationChart>;
